import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./wallpaper.module.css";
import { useDispatch, useSelector } from "react-redux";
import StarContainer from "../../components/star-container/star-container";
import Footer from "../../components/footer/footer";
import Header from "../../components/header/header";
import ScrollUpButton from "../../components/buttons/scroll-up-button/scroll-up-button";
import FullScreenModal from "../../components/savior/modal/modal-left/full-screen-modal";
import { active as activateFullScreen, inactive as deactivateFullScreen } from "../../store/reducers/toggle/fullScreenArcpintToggle";
import getImageFileName from "../../functions/getImageFileName";
import { getUIText } from "../../translation";
import { LOCALES } from "../../constants";
import { loadData } from "../../functions/dataLoader";
import SearchBox from "../../components/search-box/search-box";
import { choSearch, registQuery } from "../../functions/searchFunctions";

const PAGE_SIZE = 40;

const TAB_OBJECT = {
    ALL: { type: "ALL", name: "전체" },
    REGION: { type: "REGION", name: "지역", tabs: [] },
    PLACE: { type: "PLACE", name: "장소", tabs: [] },
    CHAR: { type: "CHAR", name: "캐릭터", tabs: [] },
    STORY: { type: "STORY", name: "스토리", tabs: [] },
    ETC: { type: "ETC", name: "기타", tabs: [] },
};

const REGIONS = ["NOA", "AGANON", "FLORA", "KALAID"];
const PLACE = ["CUBE", "STELLA_ARCHIVE", "PLAZA", "CORRIDOR", "FOREST", "HOTEL", "CANDLE_SQUARE"];
const STORY = ["MAIN", "EVENT", "JOURNEY", "CORRIDOR", "FOREST", "HOTEL", "CANDLE_SQUARE"];

export default function Wallpaper() {
    const theme = useSelector((state) => state.theme.value);
    const lang = useSelector((state) => state.lang.value);
    const dispatch = useDispatch();

    const [bgOrigin, setBgOrigin] = useState(null);
    const [text, setText] = useState("");

    useEffect(() => {
        loadData("bg_images.json").then(setBgOrigin);
    }, []);

    const subjectTabs = useMemo(() => {
        if (bgOrigin) {
            bgOrigin.forEach((e) => {
                e.tags.forEach((tag) => {
                    if (REGIONS.includes(tag.type)) {
                        const found = TAB_OBJECT.REGION.tabs.find((r) => r.type === tag.type);

                        if (!found) TAB_OBJECT.REGION.tabs.push(tag);
                    } else if (PLACE.includes(tag.type)) {
                        const found = TAB_OBJECT.PLACE.tabs.find((r) => r.type === tag.type);

                        if (!found) TAB_OBJECT.PLACE.tabs.push(tag);
                    } else if (STORY.includes(tag.type)) {
                        const found = TAB_OBJECT.STORY.tabs.find((r) => r.type === tag.type);

                        if (!found) TAB_OBJECT.STORY.tabs.push(tag);
                    } else if (tag.type === "CHAR") {
                        tag.searchText = Object.values(tag.name).join(" ");
                    } else {
                        const found = TAB_OBJECT.ETC.tabs.find((r) => r.type === tag.type);

                        if (!found) TAB_OBJECT.ETC.tabs.push(tag);
                    }
                });
            });
        }

        return TAB_OBJECT;
    }, [bgOrigin]);
    const [selectedSubjectTab, setSelectedSubjectTab] = useState(subjectTabs.ALL);
    const [selectedSubTab, setSelectedSubTab] = useState(null);

    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        if (selectedSubjectTab.type === "ALL" || selectedSubjectTab.type === "CHAR") {
            setSelectedSubTab(null);
        } else {
            setSelectedSubTab(selectedSubjectTab.tabs[0]);
        }
    }, [selectedSubjectTab]);

    const filteredList = useMemo(() => {
        if (bgOrigin) {
            return bgOrigin.filter((e) => {
                if (selectedSubjectTab.type === "ALL") return true;

                if (selectedSubjectTab.type === "CHAR") {
                    let found;
                    if (text === "") {
                        found = e.tags.find((t) => t.type === "CHAR");
                    } else {
                        found = e.tags.find((t) => {
                            if (t.type !== "CHAR") return false;

                            registQuery(lang, text);
                            const searchResult = choSearch(t.searchText);

                            return searchResult;
                        });
                    }

                    return found;
                }

                let hasTag = false;
                e.tags.forEach((tag) => {
                    if (tag.type === selectedSubTab?.type) hasTag = true;
                });

                return hasTag;
            });
        } else {
            return [];
        }
    }, [bgOrigin, selectedSubjectTab, selectedSubTab, text]);

    // 목록이 워낙 많아(400개+) 한 번에 전부 렌더링하면 렉이 심해서, 일정 개수만 먼저 보여주고
    // 스크롤이 하단 sentinel에 닿을 때마다 더 불러온다. 필터가 바뀌면 처음 페이지로 리셋.
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

    useEffect(() => {
        setVisibleCount(PAGE_SIZE);
    }, [selectedSubjectTab, selectedSubTab, text]);

    const visibleList = useMemo(() => filteredList.slice(0, visibleCount), [filteredList, visibleCount]);

    const sentinelRef = useRef(null);

    useEffect(() => {
        const target = sentinelRef.current;
        if (!target) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleCount((prev) => Math.min(prev + PAGE_SIZE, filteredList.length));
                }
            },
            { rootMargin: "600px" },
        );

        observer.observe(target);

        return () => observer.disconnect();
    }, [filteredList.length]);

    const downloadImage = (wallpaper) => {
        setIsDownloading(true);

        try {
            const link = document.createElement("a");
            link.download = `${getImageFileName(wallpaper.tags.map((e) => e.name[lang]).join("_"))}.png`;
            link.href = `${process.env.PUBLIC_URL}/images/illust/bg/download/${wallpaper.download}`;
            link.click();
        } finally {
            setIsDownloading(false);
        }
    };

    const [previewSrc, setPreviewSrc] = useState(null);

    useEffect(() => {
        dispatch(deactivateFullScreen());
    }, [dispatch]);

    const openPreview = (wallpaper) => {
        setPreviewSrc(`${process.env.PUBLIC_URL}/images/illust/bg/download/${wallpaper.download}`);
        dispatch(activateFullScreen());
    };

    return (
        <div id="container" data-theme={theme} data-lang={lang}>
            {theme === "dark" && <StarContainer />}
            <Header title={getUIText(lang, "header.title.wallpaper")} />
            <div className={styles["wallpaper-container"]}>
                <div className={styles["tabs"]}>
                    {Object.keys(subjectTabs).map((tabType) => {
                        return (
                            <button
                                className={`${styles["tab"]} ${selectedSubjectTab.type === tabType ? `${styles["active"]}` : ""}`}
                                onClick={() => setSelectedSubjectTab(subjectTabs[tabType])}
                                key={tabType}
                            >
                                {lang === LOCALES.KO ? subjectTabs[tabType].name : subjectTabs[tabType].type}
                            </button>
                        );
                    })}
                </div>
                {selectedSubjectTab.tabs && selectedSubjectTab.tabs.length > 0 && (
                    <div className={styles["tabs"]}>
                        {selectedSubjectTab.tabs.map((tab) => {
                            return (
                                <button
                                    className={`${styles["tab"]} ${selectedSubTab?.type === tab.type ? `${styles["active"]}` : ""}`}
                                    onClick={() => setSelectedSubTab(tab)}
                                    key={`SUB_TAB_NAME_${tab.type}`}
                                >
                                    {tab.name[lang]}
                                </button>
                            );
                        })}
                    </div>
                )}
                {selectedSubjectTab.type === "CHAR" && (
                    <div className={styles["tabs"]}>
                        <SearchBox changeQuery={setText} placeholder={getUIText(lang, "search.bg")} />
                    </div>
                )}
                <div className={styles["wallpapers"]}>
                    {visibleList.map((wallpaper) => {
                        const origin = wallpaper.download.substring(0, wallpaper.download.lastIndexOf("."));
                        return (
                            <div className={styles["wallpaper"]} key={wallpaper.download}>
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/illust/bg/${origin}.webp`}
                                    loading="lazy"
                                    onClick={() => openPreview(wallpaper)}
                                />
                                <div className={styles["wallpaper-info"]}>
                                    <div className={styles["size"]}>
                                        {wallpaper.size.width}x{wallpaper.size.height}
                                    </div>
                                </div>
                                <div className={styles["tags"]}>
                                    {wallpaper.tags.map((tag) => {
                                        return (
                                            <div className={styles["tag"]} key={`${wallpaper.download}_${tag.name[lang]}`}>
                                                #{tag.name[lang]}
                                            </div>
                                        );
                                    })}
                                </div>
                                <button
                                    type="button"
                                    className={styles["download"]}
                                    disabled={isDownloading}
                                    onClick={() => downloadImage(wallpaper)}
                                >
                                    {getUIText(lang, "wallpaper.download")}
                                </button>
                            </div>
                        );
                    })}
                </div>
                {visibleCount < filteredList.length && <div ref={sentinelRef} className={styles["sentinel"]} />}
            </div>

            <FullScreenModal lang={lang} imgSrc={previewSrc} />

            <ScrollUpButton />
            <Footer />
        </div>
    );
}
